import { Box, Divider, Menu, MenuItem } from '@mui/material';
import { LogOut } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import BreadcrumbButton from '~/components/Buttons/BreadcrumbButton';
import ConfirmationDialog from '~/components/Dialogs/ConfirmationDialog';
import PatientProperties from '~/components/Properties/PatientProperties';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { Patient } from '~/domain/patient/patient';
import useUnloadPatient from '~/hooks/patient/useUnloadPatient';
import useBreadcrumbMenu from '~/hooks/useBreadcrumbMenu';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';
import { showSensitiveInfoState } from '~/state/atoms/viewerAtoms';
import { calculateAge, formatDateCompact } from '~/utils/date';
import PatientInfoHiddenChip from '../PatientInfoHiddenChip';
import SensitiveInfoToggle from './SensitiveInfoToggle';

interface PatientMenuProps {
    patient: Patient;
}

export default function PatientMenu({ patient }: PatientMenuProps) {
    const { buttonProps, menuProps, close } = useBreadcrumbMenu();
    const intl = useIntl();
    const showSensitiveInfo = useRecoilValue(showSensitiveInfoState);
    const setShowSensitiveInfo = useSetRecoilState(showSensitiveInfoState);
    const hasUnsavedChanges = useRecoilValue(hasUnsavedChangesState);
    const { unloadCurrentPatient } = useUnloadPatient();
    const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

    const patientName = patient.dataSet.PatientName || 'Patient';
    const patientId = patient.dataSet.PatientID || 'N/A';
    const patientSexCode = patient.dataSet.PatientSex;
    const patientDob = patient.dataSet.PatientBirthDate;
    const formattedDob = patientDob ? formatDateCompact(patientDob) : undefined;
    const age = patientDob ? calculateAge(patientDob) : undefined;
    const dobWithAge =
        formattedDob && age !== undefined
            ? `${formattedDob} (${age}y)`
            : formattedDob;

    const sexMessageIds: Record<string, string> = {
        M: 'sex.male',
        F: 'sex.female',
        O: 'sex.other',
    };
    const patientSex = patientSexCode
        ? intl.formatMessage({
              id: sexMessageIds[patientSexCode.toUpperCase()] || 'sex.unknown',
          })
        : undefined;

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.ctrlKey && event.key.toLowerCase() === 'h') {
                // preventDefault stops the input's Ctrl+H = backspace behavior
                // (and the browser's history-panel shortcut). stopPropagation
                // would be redundant because capture-phase listeners run
                // before the target's own handlers.
                event.preventDefault();
                setShowSensitiveInfo((prev) => !prev);
            }
        };
        // Capture phase so the toggle fires even when focus is in a text
        // input that calls stopPropagation in its own onKeyDown.
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => {
            document.removeEventListener('keydown', handleKeyDown, {
                capture: true,
            });
        };
    }, [setShowSensitiveInfo]);

    const handleClosePatient = useCallback(() => {
        close();
        setConfirmCloseOpen(true);
    }, [close]);

    const handleConfirmClose = useCallback(() => {
        setConfirmCloseOpen(false);
        unloadCurrentPatient();
    }, [unloadCurrentPatient]);

    return (
        <>
            <BreadcrumbButton
                {...buttonProps}
                data-testid="patient-breadcrumb"
                flipped
                topContent={
                    showSensitiveInfo ? (
                        <SensitiveInfoTypography>
                            {patientName}
                        </SensitiveInfoTypography>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                width: '100%',
                            }}
                        >
                            <PatientInfoHiddenChip />
                        </Box>
                    )
                }
                label={
                    showSensitiveInfo ? (
                        <SensitiveInfoTypography
                            sx={{ display: 'flex', alignItems: 'center' }}
                        >
                            {[patientSex, patientId, dobWithAge]
                                .filter(Boolean)
                                .flatMap((item, index, arr) =>
                                    index < arr.length - 1
                                        ? [
                                              item,
                                              <Divider
                                                  key={`divider-${index}`}
                                                  orientation="vertical"
                                                  flexItem
                                                  sx={{
                                                      mx: 0.5,
                                                      height: 12,
                                                  }}
                                              />,
                                          ]
                                        : [item]
                                )}
                        </SensitiveInfoTypography>
                    ) : null
                }
            />

            <Menu {...menuProps}>
                <Box position="relative" px={1} pt={1} pr={5}>
                    <Box
                        sx={
                            !showSensitiveInfo
                                ? {
                                      filter: 'blur(0.5rem)',
                                      userSelect: 'none',
                                  }
                                : undefined
                        }
                    >
                        <PatientProperties
                            dataSet={patient.dataSet}
                            showTitle={false}
                        />
                    </Box>
                    <Box position="absolute" top={0} right={0}>
                        <SensitiveInfoToggle />
                    </Box>
                </Box>

                <Divider flexItem sx={{ my: 0.5 }} />

                <MenuItem
                    onClick={handleClosePatient}
                    sx={{ fontWeight: 500 }}
                    data-testid="close-patient-menu-item"
                >
                    <FormattedMessage id="closePatient" />
                    <LogOut
                        style={{ marginLeft: 'auto', fontSize: 12 }}
                        size={12}
                    />
                </MenuItem>
            </Menu>

            <ConfirmationDialog
                open={confirmCloseOpen}
                onCancel={() => setConfirmCloseOpen(false)}
                onConfirm={handleConfirmClose}
                title={intl.formatMessage({ id: 'closePatient' })}
                message={intl.formatMessage({
                    id: hasUnsavedChanges
                        ? 'closePatientConfirmationUnsaved'
                        : 'closePatientConfirmation',
                })}
            />
        </>
    );
}
